import React, { useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import scopeTab from 'react-modal/lib/helpers/scopeTab';
import classnames from 'classnames';

const CLASS_NAMES = {
  overlay: 'ReactModal__Overlay',
  content: 'ReactModal__Content',
};
const TAB_KEY = 9;
const ESC_KEY = 27;
export type Props = {
  aria?: Record<string, any>;
  children: any;
  className?: string;
  closeTimeoutMS: number;
  contentClassName: string;
  contentRef?: any;
  data?: Record<string, any>;
  isOpen: boolean;
  onRequestClose: (arg0: Event) => void;
  overlayClassName: string;
  overlayRef?: any;
  style?: {
    content?: Record<string, string | number>;
    overlay?: Record<string, string | number>;
  };
  shouldNotClose?: boolean;
};

const attributesFromObject = (prefix, items) =>
  Object.keys(items).reduce((acc, name) => {
    acc[`${prefix}-${name}`] = items[name];
    return acc;
  }, {});

// This component is intended to be rendered already open on the server.
// If you try to open it clientside, nothing will happen. The standard
// modal component using portals should be used in those instances.
const ServerModal = ({
  aria,
  children,
  className,
  closeTimeoutMS,
  contentClassName,
  contentRef,
  data,
  isOpen,
  onRequestClose,
  overlayClassName,
  overlayRef,
  shouldNotClose = false,
  style: { overlay: propOverlayStyles, content: propContentStyles } = {},
}: Props): JSX.Element | null => {
  const content = useRef<any>();
  const shouldClose = useRef<boolean | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>();
  // isVisible is different from props.isOpen in that props.isOpen
  // determines whether the modal should eventually be closed
  // (i.e. there can be a closetimeout)
  const [isVisible, setIsVisible] = useState(isOpen);
  const [beforeClose, setBeforeClose] = useState(false);
  const [closesAt, setClosesAt] = useState<number | null>(null);

  const setContentRef = (contentEl: HTMLElement | null | undefined) => {
    content.current = contentEl;

    if (contentRef) {
      contentRef(contentEl);
    }
  };

  const handleOverlayOnClick = (event: any) => {
    if (shouldNotClose === true) {
      return;
    }
    if (shouldClose.current === null) {
      shouldClose.current = true;
    }

    if (shouldClose.current) {
      onRequestClose(event);
    }

    shouldClose.current = null;
  };

  const handleKeyDown = (event: any) => {
    if (event.keyCode === TAB_KEY) {
      scopeTab(content.current, event);
    }

    if (event.keyCode === ESC_KEY) {
      event.stopPropagation();
      onRequestClose(event);
    }
  };

  const handleContentInteraction = () => {
    shouldClose.current = false;
  };

  const closeWithTimeout = () => {
    const futureTime = Date.now() + closeTimeoutMS;
    setBeforeClose(true);
    setClosesAt(futureTime);
  };

  const closeWithoutTimeout = () => {
    setBeforeClose(false);
    setIsVisible(false);
    setClosesAt(null);
  };

  const shouldBeClosed = !isVisible && !beforeClose;

  const buildClassName = (which: keyof typeof CLASS_NAMES, additional: string) =>
    classnames(CLASS_NAMES[which], {
      [`${CLASS_NAMES[which]}--after-open`]: isVisible,
      [`${CLASS_NAMES[which]}--before-close`]: beforeClose,
      [`${additional}`]: true,
    });

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    isOpen && content.current && content.current.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) {
      if (closeTimeoutMS > 0) {
        closeWithTimeout();
      } else {
        closeWithoutTimeout();
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (beforeClose && closesAt) {
      closeTimer.current = setTimeout(closeWithoutTimeout, closesAt - Date.now());
    }

    return () => clearTimeout(closeTimer.current);
  }, [beforeClose, closesAt]);
  const contentStyles = contentClassName ? {} : ReactModal.defaultStyles.content;
  const overlayStyles = overlayClassName ? {} : ReactModal.defaultStyles.overlay;
  return shouldBeClosed ? null : (
    <div className={className}>
      {/* We prefer not to disable jsx-a11y callouts; here the key event handler is in the inner div and the overlay needs a click handler for dismissal */}
      <div // eslint-disable-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        ref={overlayRef}
        className={buildClassName('overlay', overlayClassName)}
        style={{ ...overlayStyles, ...propOverlayStyles }}
        onClick={handleOverlayOnClick}
      >
        {/* We prefer not to disable jsx-a11y callouts, but this may be spurious - div role="dialog" is interactive */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <div
          ref={setContentRef}
          style={{ ...contentStyles, ...propContentStyles }}
          className={buildClassName('content', contentClassName)}
          role="dialog"
          onKeyDown={handleKeyDown}
          onMouseDown={handleContentInteraction}
          onMouseUp={handleContentInteraction}
          onClick={handleContentInteraction}
          {...attributesFromObject('data', data || {})}
          {...attributesFromObject('aria', { ...aria, modal: true })}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default ServerModal;
